import {
  Button,
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
  FieldLegend,
  FieldSet,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Textarea,
} from '@monksflow/monks-ui';
import { PlusIcon, Trash2Icon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type {
  FieldErrors,
  JsonSchema,
  SchemaWidget,
  SchemaWidgetProps,
  UiSchema,
} from './SchemaForm.types';
import {
  type FieldKind,
  emptyValueFor,
  getFieldKind,
  getFieldLabel,
  getUiSchemaFor,
  getWidgetName,
  joinPath,
  pathToId,
  unwrapNullable,
  withDefault,
} from './schemaUtils';

export interface SchemaFieldProps {
  name: string;
  path: string;
  id: string;
  schema: JsonSchema;
  uiSchema?: UiSchema;
  value: unknown;
  onChange: (value: unknown) => void;
  required: boolean;
  disabled: boolean;
  widgets: Record<string, SchemaWidget>;
  errors: FieldErrors;
  idPrefix: string;
  /** Overrides the label derived from `title`/property name (array items, record entries). */
  label?: string;
  /** Keeps the label available to assistive tech while removing it from the layout. */
  hideLabel?: boolean;
}

type GroupKind = 'object' | 'array' | 'map';
type GroupProps = SchemaFieldProps & { label: string };

const NEW_ENTRY_KEY = 'newKey';

export function SchemaField(props: SchemaFieldProps) {
  const { t } = useTranslation('common');

  const { uiSchema, widgets, name, path, errors, id, required, hideLabel } = props;
  const schema = unwrapNullable(props.schema);
  const label = props.label ?? getFieldLabel(schema, name);
  const fieldErrors = errors[path] ?? [];
  const kind = getFieldKind(props.schema, uiSchema, widgets);

  if (kind === 'unsupported') {
    return (
      <div
        data-testid="schema-unsupported-field"
        className="rounded-md border border-border bg-muted p-3 text-sm text-muted-foreground"
      >
        {t('ui.schemaForm.unsupportedField', { label })}
      </div>
    );
  }

  if (kind === 'object' || kind === 'array' || kind === 'map') {
    return (
      <FieldSet data-testid={`schema-${kind}-field`}>
        <FieldLegend variant="label" className={hideLabel === true ? 'sr-only' : undefined}>
          {label}
          <RequiredMarker required={required} />
        </FieldLegend>
        {schema.description !== undefined && (
          <FieldDescription>{schema.description}</FieldDescription>
        )}
        <GroupEntries {...props} label={label} kind={kind} />
        {fieldErrors.length > 0 && <FieldError>{fieldErrors[0]}</FieldError>}
      </FieldSet>
    );
  }

  return (
    <Field data-invalid={fieldErrors.length > 0 || undefined}>
      <div className={hideLabel === true ? 'sr-only' : 'flex items-center gap-0.5'}>
        <FieldLabel htmlFor={id}>{label}</FieldLabel>
        <RequiredMarker required={required} />
      </div>
      <SchemaControl {...props} label={label} kind={kind} hasError={fieldErrors.length > 0} />
      {schema.description !== undefined && (
        <FieldDescription>{schema.description}</FieldDescription>
      )}
      {fieldErrors.length > 0 && <FieldError>{fieldErrors[0]}</FieldError>}
    </Field>
  );
}

/**
 * Sits outside the `<label>`/`<legend>` text, or is `aria-hidden` inside it, so the
 * control's accessible name stays the bare field label. An asterisk read aloud is noise.
 */
function RequiredMarker({ required }: { required: boolean }) {
  if (!required) return null;
  return (
    <span aria-hidden="true" className="text-destructive">
      *
    </span>
  );
}

function GroupEntries(props: GroupProps & { kind: GroupKind }) {
  switch (props.kind) {
    case 'object':
      return <ObjectEntries {...props} />;
    case 'array':
      return <ArrayEntries {...props} />;
    default:
      return <RecordEntries {...props} />;
  }
}

function SchemaControl(props: GroupProps & { kind: FieldKind; hasError: boolean }) {
  const { t } = useTranslation('common');

  const {
    id,
    name,
    label,
    value,
    onChange,
    disabled,
    hasError,
    kind,
    widgets,
    uiSchema,
    required,
  } = props;
  const schema = unwrapNullable(props.schema);
  const current = withDefault(schema, value);
  // The asterisk next to the label is aria-hidden, so this is the only thing
  // that tells assistive tech the field is required before validation runs.
  const ariaRequired = required || undefined;

  if (kind === 'widget') {
    const widgetName = getWidgetName(uiSchema) ?? '';
    const Widget = widgets[widgetName];
    const widgetProps: SchemaWidgetProps = {
      id,
      name,
      label,
      value: current,
      onChange,
      schema,
      required,
      disabled,
      hasError,
    };
    return <Widget {...widgetProps} />;
  }

  if (kind === 'enum') {
    const options = schema.enum ?? [];
    const selected = options.find((option) => option === current);
    return (
      <Select
        value={selected === undefined ? undefined : String(selected)}
        onValueChange={(next) => onChange(options.find((option) => String(option) === next))}
        disabled={disabled}
      >
        <SelectTrigger
          id={id}
          error={hasError}
          aria-invalid={hasError || undefined}
          aria-required={ariaRequired}
        >
          <SelectValue placeholder={t('ui.schemaForm.selectAnOption')} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={String(option)} value={String(option)}>
              {String(option)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  if (kind === 'boolean') {
    return (
      <Switch
        id={id}
        checked={current === true}
        onCheckedChange={(next) => onChange(next)}
        disabled={disabled}
        aria-invalid={hasError || undefined}
        aria-required={ariaRequired}
      />
    );
  }

  if (kind === 'number') {
    return (
      <Input
        id={id}
        type="number"
        value={typeof current === 'number' ? String(current) : ''}
        onChange={(event) => {
          const raw = event.target.value;
          onChange(raw === '' ? undefined : Number(raw));
        }}
        disabled={disabled}
        aria-invalid={hasError || undefined}
        aria-required={ariaRequired}
      />
    );
  }

  const text = typeof current === 'string' ? current : '';
  const reportText = (next: string) => onChange(next === '' ? undefined : next);

  if (kind === 'textarea') {
    return (
      <Textarea
        id={id}
        value={text}
        onChange={(event) => reportText(event.target.value)}
        disabled={disabled}
        aria-invalid={hasError || undefined}
        aria-required={ariaRequired}
      />
    );
  }

  return (
    <Input
      id={id}
      type="text"
      value={text}
      onChange={(event) => reportText(event.target.value)}
      disabled={disabled}
      aria-invalid={hasError || undefined}
      aria-required={ariaRequired}
    />
  );
}

function ObjectEntries(props: GroupProps) {
  const { path, value, onChange, uiSchema, idPrefix } = props;
  const schema = unwrapNullable(props.schema);
  const properties = schema.properties ?? {};
  const record = isRecord(value) ? value : {};
  const requiredKeys = new Set(schema.required ?? []);

  return (
    <div className="flex flex-col gap-5 border-l border-border pl-4">
      {Object.keys(properties).map((key) => {
        const childPath = joinPath(path, key);
        return (
          <SchemaField
            {...props}
            key={key}
            name={key}
            label={undefined}
            hideLabel={false}
            path={childPath}
            id={pathToId(idPrefix, childPath)}
            schema={properties[key]}
            uiSchema={getUiSchemaFor(uiSchema, key)}
            value={record[key]}
            required={requiredKeys.has(key)}
            onChange={(next) => onChange({ ...record, [key]: next })}
          />
        );
      })}
    </div>
  );
}

function ArrayEntries(props: GroupProps) {
  const { t } = useTranslation('common');

  const { path, value, onChange, uiSchema, idPrefix, disabled, label } = props;
  const schema = unwrapNullable(props.schema);
  const itemSchema = schema.items ?? {};
  const items = Array.isArray(value) ? (value as unknown[]) : [];

  return (
    <div className="flex flex-col gap-3">
      {items.length === 0 && <FieldDescription>{t('ui.schemaForm.noEntries')}</FieldDescription>}
      {items.map((item, index) => {
        const childPath = joinPath(path, index);
        const itemLabel = t('ui.schemaForm.itemLabel', { label, index: index + 1 });
        return (
          <div
            key={index}
            data-testid="schema-array-item"
            className="flex items-start gap-2 border-l border-border pl-4"
          >
            <div className="flex-1">
              <SchemaField
                {...props}
                name={String(index)}
                label={itemLabel}
                hideLabel
                path={childPath}
                id={pathToId(idPrefix, childPath)}
                schema={itemSchema}
                // eslint-disable-next-line i18next/no-literal-string -- JSON Schema keyword
                uiSchema={getUiSchemaFor(uiSchema, 'items')}
                value={item}
                required={false}
                onChange={(next) =>
                  onChange(items.map((entry, position) => (position === index ? next : entry)))
                }
              />
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              disabled={disabled}
              aria-label={t('ui.schemaForm.removeEntry', { label: itemLabel })}
              onClick={() => onChange(items.filter((_, position) => position !== index))}
            >
              <Trash2Icon aria-hidden="true" className="size-4" />
            </Button>
          </div>
        );
      })}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="self-start"
        disabled={disabled}
        onClick={() => onChange([...items, emptyValueFor(itemSchema)])}
      >
        <PlusIcon aria-hidden="true" className="size-4" />
        {t('ui.schemaForm.addEntry', { label })}
      </Button>
    </div>
  );
}

function RecordEntries(props: GroupProps) {
  const { t } = useTranslation('common');

  const { path, value, onChange, uiSchema, idPrefix, disabled, label } = props;
  const schema = unwrapNullable(props.schema);
  const entrySchema =
    typeof schema.additionalProperties === 'object' ? schema.additionalProperties : {};
  const entries = Object.entries(isRecord(value) ? value : {});
  const entryUiSchema = getUiSchemaFor(uiSchema, 'additionalProperties');

  const rebuild = (next: [string, unknown][]) => onChange(Object.fromEntries(next));

  const renameAt = (index: number, nextKey: string) => {
    if (entries.some(([key], position) => position !== index && key === nextKey)) return;
    rebuild(
      entries.map(([key, entry], position) =>
        position === index ? [nextKey, entry] : [key, entry],
      ),
    );
  };

  const addEntry = () => {
    const taken = new Set(entries.map(([key]) => key));
    let key = NEW_ENTRY_KEY;
    let suffix = 2;
    while (taken.has(key)) {
      key = `${NEW_ENTRY_KEY}${suffix}`;
      suffix += 1;
    }
    rebuild([...entries, [key, emptyValueFor(entrySchema)]]);
  };

  return (
    <div className="flex flex-col gap-3">
      {entries.length === 0 && <FieldDescription>{t('ui.schemaForm.noEntries')}</FieldDescription>}
      {entries.map(([key, entryValue], index) => {
        const childPath = joinPath(path, key);
        return (
          <div
            key={index}
            data-testid="schema-map-entry"
            className="flex items-start gap-2 border-l border-border pl-4"
          >
            <Input
              className="w-48 shrink-0"
              aria-label={t('ui.schemaForm.entryKey', { key })}
              value={key}
              disabled={disabled}
              onChange={(event) => renameAt(index, event.target.value)}
            />
            <div className="flex-1">
              <SchemaField
                {...props}
                name={key}
                label={key}
                hideLabel
                path={childPath}
                id={pathToId(idPrefix, childPath)}
                schema={entrySchema}
                uiSchema={entryUiSchema}
                value={entryValue}
                required={false}
                onChange={(next) =>
                  rebuild(
                    entries.map(([existingKey, existingValue], position) =>
                      position === index ? [existingKey, next] : [existingKey, existingValue],
                    ),
                  )
                }
              />
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              disabled={disabled}
              aria-label={t('ui.schemaForm.removeEntry', { label: key })}
              onClick={() => rebuild(entries.filter((_, position) => position !== index))}
            >
              <Trash2Icon aria-hidden="true" className="size-4" />
            </Button>
          </div>
        );
      })}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="self-start"
        disabled={disabled}
        onClick={addEntry}
      >
        <PlusIcon aria-hidden="true" className="size-4" />
        {t('ui.schemaForm.addEntry', { label })}
      </Button>
    </div>
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
